import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, User, ChevronRight, Sparkles, Rocket, Loader2 
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const OnboardingWizard = ({ onComplete }) => {
  const { updateProfile } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage(); // Dil fonksiyonunu al
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState({
    businessType: 'freelancer', // freelancer | company
    companyName: '',
    street: '',
    zipCity: '',
    taxId: '',
    iban: '',
  });

  const nextStep = () => setStep(s => s + 1);
   
  const handleComplete = async () => {
    // Input Validasyonu (Basit bir kontrol)
    if (step === 3 && !data.iban) {
      toast({ 
        title: t('error_generic') || "Eksik Bilgi", 
        description: t('onb_error_iban') || "Lütfen ödemeleriniz için bir IBAN girin.", 
        variant: "destructive" 
      });
      return;
    }

    setLoading(true);
    try {
      // AuthContext üzerinden backend'e veriyi gönderiyoruz
      const result = await updateProfile({
        companyDetails: {
          companyName: data.companyName,
          street: data.street,
          zipCity: data.zipCity,
          taxId: data.taxId,
          iban: data.iban
        },
        onboarding: { 
          completed: true, 
          businessType: data.businessType, 
          completedAt: new Date().toISOString() 
        }
      });

      if (result.success) {
        toast({ 
          title: t('onb_toast_complete') || "Kurulum Tamamlandı!", 
          description: t('onb_toast_desc') || "Fox-AI artık senin için kişiselleştirildi." 
        });
        onComplete();
      } else {
        throw new Error(result.error || "Güncelleme başarısız.");
      }
    } catch (error) {
      toast({ 
        title: t('error_generic') || "Hata", 
        description: error.message || "Ayarlar kaydedilirken bir sorun oluştu.", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 backdrop-blur-md bg-black/60">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-xl bg-[#0F172A] border border-white/10 rounded-[3rem] shadow-2xl overflow-hidden relative"
      >
        {/* Üst Kısım: Progress Bar */}
        <div className="absolute top-0 left-0 w-full h-1 bg-white/5">
          <motion.div 
            className="h-full bg-blue-500"
            initial={{ width: "0%" }}
            animate={{ width: `${(step / 3) * 100}%` }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </div>

        <div className="p-10 space-y-8">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div 
                key="step1"
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: -20 }}
                className="space-y-8"
              >
                <div className="space-y-2">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-500 mb-4">
                    <Sparkles size={24} />
                  </div>
                  <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter tracking-widest">{t('onb_welcome') || 'Willkommen!'}</h2>
                  <p className="text-slate-400 font-medium leading-relaxed">
                    {t('onb_desc') || 'Lass uns dein BüroFuchs Profil einrichten.'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { id: 'freelancer', label: t('onb_freelancer') || 'Freelancer', icon: User },
                    { id: 'company', label: t('onb_company') || 'GmbH / UG', icon: Building2 }
                  ].map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setData({ ...data, businessType: type.id })}
                      className={`p-6 rounded-[2rem] border-2 transition-all duration-300 flex flex-col items-center gap-4 group ${
                        data.businessType === type.id 
                        ? 'border-blue-500 bg-blue-500/10 text-white shadow-[0_0_20px_rgba(59,130,246,0.2)]' 
                        : 'border-white/5 bg-white/5 text-slate-500 hover:border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <type.icon size={32} className={data.businessType === type.id ? 'text-blue-400' : 'group-hover:text-slate-300'} />
                      <span className="font-black uppercase text-[10px] tracking-widest">{type.label}</span>
                    </button>
                  ))}
                </div>
                <Button 
                  onClick={nextStep} 
                  className="w-full h-14 rounded-2xl bg-blue-600 hover:bg-blue-500 font-black uppercase tracking-widest transition-all"
                >
                  {t('onb_next') || 'Weiter'} <ChevronRight size={18} className="ml-2"/>
                </Button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div 
                key="step2"
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: -20 }}
                className="space-y-6"
              >
                <div className="space-y-1">
                  <h3 className="text-xl font-black text-white italic uppercase tracking-tight">{t('onb_company_data') || 'Firmendaten'}</h3>
                  <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">{t('onb_company_desc') || 'Fatura detayların için yasal bilgiler.'}</p>
                </div>
                
                <div className="space-y-4">
                  <Input 
                    placeholder={t('onb_name') || "Name / Firma"} 
                    value={data.companyName} 
                    onChange={e => setData({...data, companyName: e.target.value})}
                    className="h-14 bg-white/5 border-white/10 rounded-xl text-white focus:border-blue-500/50"
                  />
                  <Input 
                    placeholder={t('onb_street') || "Straße & Nr."} 
                    value={data.street}
                    onChange={e => setData({...data, street: e.target.value})}
                    className="h-14 bg-white/5 border-white/10 rounded-xl text-white focus:border-blue-500/50"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Input 
                      placeholder={t('onb_zip') || "PLZ & Stadt"} 
                      value={data.zipCity}
                      onChange={e => setData({...data, zipCity: e.target.value})}
                      className="h-14 bg-white/5 border-white/10 rounded-xl text-white focus:border-blue-500/50"
                    />
                    <Input 
                      placeholder={t('onb_tax') || "Steuernummer"} 
                      value={data.taxId}
                      onChange={e => setData({...data, taxId: e.target.value})}
                      className="h-14 bg-white/5 border-white/10 rounded-xl text-white focus:border-blue-500/50"
                    />
                  </div>
                </div>
                <Button 
                  onClick={nextStep} 
                  className="w-full h-14 rounded-2xl bg-blue-600 hover:bg-blue-500 font-black uppercase tracking-widest transition-all"
                >
                  {t('onb_almost') || 'Fast geschafft'} <ChevronRight size={18} className="ml-2"/>
                </Button>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div 
                key="step3"
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: -20 }}
                className="space-y-8 text-center"
              >
                <div className="w-20 h-20 bg-emerald-500/20 text-emerald-500 rounded-full flex items-center justify-center mx-auto border border-emerald-500/20 shadow-[0_0_30px_rgba(16,185,129,0.1)]">
                  <Rocket size={40} className="animate-bounce" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter">{t('onb_ready') || 'Bereit zum Start!'}</h3>
                  <p className="text-slate-400 font-medium">{t('onb_secure') || 'Deine Daten werden sicher verschlüsselt gespeichert.'}</p>
                </div>
                <div className="space-y-4">
                    <Input 
                        placeholder={t('onb_iban') || "IBAN für deine Rechnungen"} 
                        value={data.iban}
                        onChange={e => setData({...data, iban: e.target.value})}
                        className="h-16 bg-white/5 border-white/10 rounded-2xl text-center font-mono text-xl text-blue-400 focus:border-blue-500/50 tracking-wider"
                    />
                </div>
                <Button 
                    onClick={handleComplete} 
                    disabled={loading}
                    className="w-full h-16 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 font-black uppercase tracking-widest shadow-xl shadow-blue-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
                >
                    {loading ? <Loader2 className="animate-spin" /> : (t('onb_finish') || 'Dashboard betreten')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingWizard;